import React, { useState, useEffect } from 'react';
import { 
  MoreVertical, 
  CheckCircle, 
  Play, 
  Pause, 
  Clock, 
  Edit, 
  X, 
  AlertCircle 
} from 'lucide-react';

export default function TaskCard({ 
  task, 
  onComplete, 
  onDismiss, 
  onSnooze, 
  onDelete, 
  onEdit, 
  onStatusChange,
  isHighlighted = false
}) {
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);


  // Safety check for task object
  if (!task) {
    console.error('TaskCard: task is undefined or null');
    return null;
  }

  const isSmartTask = task.source === 'auto_generated';

  // Close actions menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionsMenu && !event.target.closest('.actions-menu-container')) {
        setShowActionsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActionsMenu]);

  let suggestedActions = [];
  try {
    if (task.suggested_actions) {
      suggestedActions = typeof task.suggested_actions === 'string' 
        ? JSON.parse(task.suggested_actions) 
        : task.suggested_actions;
    }
  } catch (error) {
    console.error('Error parsing suggested_actions:', error);
    suggestedActions = [];
  }

  // Status colors
  const statusColors = {
    not_started: 'text-red-700 bg-gradient-to-r from-red-100 to-red-200',
    in_progress: 'text-blue-700 bg-gradient-to-r from-blue-100 to-blue-200',
    completed: 'text-green-700 bg-gradient-to-r from-green-100 to-green-200'
  };

  const statusLabels = {
    not_started: 'Not Started',
    in_progress: 'In Progress',
    completed: 'Completed'
  };

  // Priority badge colors
  const priorityBadgeColors = {
    high: 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-sm',
    medium: 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm',
    low: 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm'
  };

  const handleStart = () => {
    if (onStatusChange) {
      onStatusChange(task.id, 'in_progress');
    }
    setShowActionsMenu(false);
  };

  const handlePause = () => {
    if (onStatusChange) {
      onStatusChange(task.id, 'not_started');
    }
    setShowActionsMenu(false);
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete(task.id);
    }
    setShowActionsMenu(false);
  };

  const handleReopen = () => {
    if (onStatusChange) {
      onStatusChange(task.id, 'in_progress');
    }
    setShowActionsMenu(false);
  };

  return (
    <div className="group relative">
      <div className={`bg-white border rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden ${
        isHighlighted 
          ? 'border-blue-500 border-2 bg-blue-50 shadow-lg ring-2 ring-blue-200' 
          : 'border-gray-200'
      }`}>
        {/* Priority indicator bar */}
        <div className="h-1 w-full bg-gray-300"></div>
        
        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {/* Task icon */}
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                task.priority === 'high' ? 'bg-gradient-to-br from-red-100 to-red-200' :
                task.priority === 'medium' ? 'bg-gradient-to-br from-blue-100 to-blue-200' :
                'bg-gradient-to-br from-green-100 to-green-200'
              }`}>
                <span className="text-sm">
                  {task.category === 'daily_engagement' ? '🤖' :
                   task.invoice_id ? '📄' : '📝'}
                </span>
              </div>
              
              {/* Title and badges */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-1">
                  {task.title}
                </h3>
                
                <div className="flex items-center gap-1 flex-wrap">
                  <span className={`px-1 py-0.5 rounded-full text-[0.65rem] font-medium ${statusColors[task.status]}`}>
                    {statusLabels[task.status]}
                  </span>
                  <span className={`px-1 py-0.5 rounded-full text-[0.65rem] font-medium ${priorityBadgeColors[task.priority]}`}>
                    {task.priority.toUpperCase()}
                  </span>
                  {isSmartTask && (
                    <span className="px-1 py-0.5 rounded-full text-[0.65rem] font-medium bg-gradient-to-r from-purple-100 to-purple-200 text-purple-700">
                      Smart
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions menu */}
            <div className="relative ml-4 flex-shrink-0 actions-menu-container">
              <button
                onClick={() => setShowActionsMenu(!showActionsMenu)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200 rounded-lg hover:bg-gray-100"
                title="Task actions"
              >
                <MoreVertical className="h-5 w-5" />
              </button>

              {/* Actions Dropdown - Use fixed positioning to prevent overflow issues */}
              {showActionsMenu && (
                <div className="fixed mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-[9999]" 
                     style={{ 
                       top: 'auto',
                       right: 'auto',
                       transform: 'translateY(8px)'
                     }}
                     ref={(el) => {
                       if (el) {
                         const button = el.previousElementSibling;
                         if (button) {
                           const rect = button.getBoundingClientRect();
                           el.style.top = `${rect.bottom + window.scrollY}px`;
                           el.style.left = `${rect.right - el.offsetWidth + window.scrollX}px`;
                         }
                       }
                     }}>
                  <div className="py-1">
                    {task.status === 'not_started' && (
                      <button
                        onClick={handleStart}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Play className="h-4 w-4" />
                        Start Task
                      </button>
                    )}
                    
                    {task.status === 'in_progress' && (
                      <>
                        <button
                          onClick={handleComplete}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <CheckCircle className="h-4 w-4" />
                          Complete Task
                        </button>
                        <button
                          onClick={handlePause}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <Pause className="h-4 w-4" />
                          Pause Task
                        </button>
                      </>
                    )}

                    {task.status === 'completed' && (
                      <button
                        onClick={handleReopen}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Play className="h-4 w-4" />
                        Reopen Task
                      </button>
                    )}

                    <div className="border-t border-gray-100"></div>
                    
                    <button
                      onClick={() => {
                        if (onSnooze) onSnooze(24);
                        setShowActionsMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Clock className="h-4 w-4" />
                      Snooze 1 day
                    </button>
                    
                    <button
                      onClick={() => {
                        if (onSnooze) onSnooze(168);
                        setShowActionsMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Clock className="h-4 w-4" />
                      Snooze 1 week
                    </button>

                    {!isSmartTask && (
                      <button
                        onClick={() => {
                          if (onEdit) onEdit(task);
                          setShowActionsMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Edit Task
                      </button>
                    )}

                    <div className="border-t border-gray-100"></div>
                    
                    <button
                      onClick={() => {
                        if (isSmartTask && onDismiss) {
                          onDismiss(task.id);
                        } else if (!isSmartTask && onDelete) {
                          onDelete(task.id);
                        }
                        setShowActionsMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <X className="h-4 w-4" />
                      {isSmartTask ? 'Dismiss' : 'Delete'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {task.description && (
            <div className="mb-3">
              <p className="text-xs text-gray-600 leading-relaxed">
                {isExpanded ? task.description : 
                 task.description.length > 120 ? 
                 task.description.substring(0, 120) + '...' : 
                 task.description}
              </p>
              {task.description.length > 120 && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-blue-600 hover:text-blue-700 text-xs font-medium mt-1"
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-3">
              {task.due_date && (
                <div className="flex items-center gap-1">
                  <span>📅</span>
                  <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <span>🏷️</span>
                <span className="capitalize">{task.source.replace('_', ' ')}</span>
              </div>
            </div>
            
            {/* Progress indicator for smart tasks */}
            {isSmartTask && task.progress_current && task.progress_total && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  {task.progress_current}/{task.progress_total}
                </span>
                <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
                    style={{ width: `${(task.progress_current / task.progress_total) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Suggested Actions */}
          {suggestedActions && Array.isArray(suggestedActions) && suggestedActions.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex flex-wrap gap-1">
                {suggestedActions.slice(0, 3).map((action, index) => {
                  // Handle both string and object formats
                  const actionText = typeof action === 'string' ? action : (action?.label || action?.text || JSON.stringify(action));
                  return (
                    <span
                      key={index}
                      className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200"
                    >
                      {actionText}
                    </span>
                  );
                })}
                {suggestedActions.length > 3 && (
                  <span className="px-2 py-0.5 bg-gray-50 text-gray-600 text-xs rounded-full">
                    +{suggestedActions.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}